import { useAutocomplete, Box, IconButton, alpha, TextField as MuiTextField } from "@mui/material"
import ClearIcon from '@mui/icons-material/Clear';
import { ListControllerProps, useListContext } from "ra-core"
import { useRef, useCallback, useEffect, useMemo, useState, MutableRefObject, Dispatch, SetStateAction } from "react"
import { RecPatch } from "@/dataProvider/tools"
import { EncryptionRuleResponseDto, MaskingRule } from "@/gen/api/encrypt"
import { FieldMappingInput, FieldMappingResponseDto, EncryptMappingStatus } from "@/gen/api/governance"
import { FieldMappingCatagory } from "@/pages/obfuscation/mutateEncryptionMappingsController"
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { FormNode, FormState, FormToken, useZTreeStoreContext } from "@/utils/zustand-form";
import { runOtherThread } from "@/utils/asyncUtils";
import { CollectEditsProtocol } from "@/utils/zustand-form/ext/collectEdits";
import { useEventBus } from "@/contexts/EventBusContext/useEventBus";
import { E_FieldMapping, emsChangePayload, ET_FieldMapping } from "./FieldsMappingList.events";
import { debounce } from "lodash";
import { ZExtensionProtocol } from "@/utils/zustand-form/types";


export type SelectedEncryptor = '' | 'skip' | EncryptionRuleResponseDto

export const buildInitialValue = (field:FieldMappingResponseDto) => ({
  ...field,
  fieldId: field.fieldId,
  isSkipped: field.encryptMappingStatus === EncryptMappingStatus.Skipped,
  secondaryClassificationId: field.secondaryClassificationId,
  maskingRule: 
    field.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour ? MaskingRule.ReplaceWithCharacter 
    : field.maskingRule === MaskingRule.None ? MaskingRule.None
    : (field.maskingRule || ''),
  ...({
    preserveLast4: field.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour,
  } as any),
  maskingValue: field.maskingValue || '',
})

type RootState = FormState<any, any> & { selectedIds: Set<number> };
type RowState = FormState<FieldMappingInput, FieldMappingResponseDto>;

interface UseModifiersControl {
  isRowSelectable: (r: FieldMappingResponseDto & RecPatch, isSelectAllContext?: boolean) => boolean;
  discardAllEdits: (mergeDelta?: boolean) => void;
  bulkSkipMappings: BulkSkipMappings;
  setSkipStateAction: (v: RowState) => RowState;
}
type BulkSkipMappings = (toSkipProvider?: (()=>RowState[])) => () => FieldMappingInput[];

export const useModifiers = (
  rootToken: FormToken,
): UseModifiersControl => {
  const store = useZTreeStoreContext()
  
  const eventBus = useEventBus('fields-mapping-list')
  
  const isRowSelectable = useCallback((r: FieldMappingResponseDto & RecPatch, isSelectAllContext?: boolean) => {
    if (!isSelectAllContext) return true
    const storeVal = store.getState()
    const rootNode = storeVal.getNode(rootToken.id)
    if (!rootNode) return false;
    const rowNode: FormNode<FieldMappingInput, FieldMappingResponseDto>|undefined = 
      storeVal.getNode(
        rootNode.children.get(r.fieldId+'')?.id ?? '')
    return r.encryptMappingStatus === EncryptMappingStatus.PendingReview && (
      !rowNode ||
      determineMappingCatagory(rowNode.state.stage) === 'pending-review')
  }, [rootToken.id, store])

  const discardAllEdits = useCallback((mergeDelta?: boolean) => {
    if (mergeDelta) {
      eventBus.publish(E_FieldMapping.MergeDelta)
    } else {
      eventBus.publish(E_FieldMapping.Reset)
    }
  }, [eventBus])

  const setSkipStateAction = (v: RowState): RowState => {
    // set "skip"
    const v1 : typeof v = {...v}
    const field = v.prevData
    v1.stage = {...v1.stage,
      secondaryClassificationId: 100, // Public-Default
      isSkipped: true,  
      encryptionRuleId: null,  
      maskingRule: MaskingRule.None,  
      maskingValue: '',
      ...({preserveLast4: false} as any),
    }
    v1.touched = {...v1.touched,
      secondaryClassificationId: field.secondaryClassificationId !== 100,
      encryptionRuleId: !!field.encryptionRuleId || field.encryptMappingStatus !== EncryptMappingStatus.Skipped,
      maskingRule: field.maskingRule !== MaskingRule.None,
      maskingValue: field.maskingValue !== '',
    }
    return v1
  }

  const bulkSkipMappings : BulkSkipMappings = (toSkipProvider?: ()=>RowState[]) => () => {
    const emsChanges : ET_FieldMapping['encrypt_mapping_status_change'] = []
    const publishEmsChanges = debounce(() => {
      if (emsChanges.length > 0) {
        eventBus.publish(E_FieldMapping.EncryptMappingStatusChange, [...emsChanges])
        emsChanges.length = 0
      }
    }, 20)

    const finalProvider =
      toSkipProvider ? function* () {
        for (const v of toSkipProvider()) {
          yield [v, noop] as const
        }
      } : defaultSkipProvider
    const resultls : FieldMappingInput[] = []

    for (const pair of finalProvider()) {
      const [v, set] = pair
      const v1 = setSkipStateAction(v)
      emsChanges.push(emsChangePayload(v1.prevData, v1.stage))
      publishEmsChanges()
      set(v1)
      resultls.push(v.stage)
    }
    return resultls

    function* defaultSkipProvider(): Generator<[RowState, (x:RowState)=>void], void, void> {
      const storeVal = store.getState()
      const rootNode = storeVal.getNode(rootToken.id)!
      const rootState : RootState = rootNode.state as any
      if (rootState.selectedIds.size < 1) return
      
      for (const id of rootState.selectedIds) {
        const fieldId = Number(id)
        const rowNode: FormNode<FieldMappingInput, FieldMappingResponseDto>|undefined = 
          storeVal.getNode(
            rootNode.children.get(fieldId+'')?.id ?? '')
        
        if (!rowNode) continue;
        const invokeProtocol = (protocol: ZExtensionProtocol<string, any, any>) => storeVal.invokeNodeProtocol(rowNode.token.id, protocol)
        yield [
          rowNode.state, 
          (x: RowState) => {
            storeVal.updateNode<FieldMappingInput, FieldMappingResponseDto>(rowNode.token.id, (set) => {
              set(x)
              if (invokeProtocol) {
                runOtherThread(() => {
                  // collectEdits will trigger validation
                  CollectEditsProtocol.invoke(invokeProtocol)
                })
              }
            })
          }
        ]
      }

      // onUnselectItems()
      storeVal.updateNode(rootToken.id, (set) => {
        set(v => ({...v, selectedIds: new Set<number>() }))
      })
    }

    function noop (x:RowState) {}
  }

  return {
    isRowSelectable,
    discardAllEdits,
    bulkSkipMappings,
    setSkipStateAction,
  }
}

const determineMappingCatagory = (x: { 
  isSkipped?: boolean | null, 
  encryptionRuleId?: number | null, 
  customEncryptionRuleId?: number | null 
}): FieldMappingCatagory => {
  if (x.isSkipped) {
    return 'skip'
  } else if (Number(x.encryptionRuleId) > 0 || Number(x.customEncryptionRuleId) > 0) {
    return 'mapped'
  } else {
    return 'pending-review'
  }
}


const fieldsSorter = (a:FieldDto, b:FieldDto) => a.fieldId! - b.fieldId!

interface FieldDto {
  fieldId: number;
  fieldName: string;
}

export interface BigTableModeControl {
  bigTableModeOn: boolean;
  slots: {
    SearchInput: JSX.Element | null;
    ListBox: JSX.Element | null;
    popoutOpen: boolean;
    filteredItemCount: number;
    closePopout: () => void;
  };
  editorOnlyListCtx: ListControllerProps<FieldDto & RecPatch>;
  modifiers: {
    resetStagedFields: () => void;
    addFieldToEditor: (x: FieldDto) => void;
    isStagedField: (id: number) => boolean;
    takeFilteredTrackSkip: (trackSkip: (ls: FieldDto[])=>FieldDto[]) => void;
    removeFieldFromEditor: (id: number) => void;
    getBulkSkippedData: () => ReadonlyMap<number, FieldDto>;
    getStagedData: () => ReadonlyMap<number, FieldDto>;
  };
}

export const useBigTableMode = (props: {
  isFieldMapped: (field: FieldDto) => boolean,
  onRemoveFieldFromEditor?: (fieldId: number) => void,
}): BigTableModeControl => {
  const listCtx = useListContext<FieldDto & RecPatch>()
  const { ids, data } = listCtx

  const bigTableModeOn = ids.length > 60

  const propsRef = useRef(props)
  propsRef.current = props

  const { isFieldMapped } = props

  const groupedFields = useMemo(() => {
    let groupedFields = { charted: [] as FieldDto[], uncharted: [] as FieldDto[], unchartedIds: new Set<number>()}
    if (bigTableModeOn) {
      groupedFields = ids.reduce((acc, id) => {
        const x : FieldDto = data[id]
        if (isFieldMapped(x)) {
          acc.charted.push(x)
        } else {
          acc.uncharted.push(x)
        }
        return acc
      }, groupedFields)
    }
    groupedFields.charted.sort(fieldsSorter)
    groupedFields.uncharted.sort(fieldsSorter)
    groupedFields.unchartedIds = new Set(groupedFields.uncharted.map(x => x.fieldId!))
    return groupedFields
  }, [bigTableModeOn, ids, data, isFieldMapped])
  
  const [states, setStates] = useState({
    popoutOpen: false,
    staged: {
      ids: [] as number[],
      // data: {} as Record<number, FieldDto>
    },
  })
  const stagedDataRef = useRef(new Map<number, FieldDto>())
  const bulkSkippedDataRef = useRef(new Map<number, FieldDto>())
  const groupedOptionsRef = useRef<FieldDto[]>([])

  const modifiers = useMemo(() => {
    if (!bigTableModeOn) {
      return {
        resetStagedFields: () => {},
        addFieldToEditor: (x: FieldMappingResponseDto) => {},
        removeFieldFromEditor: (id: number) => {},
        isStagedField: (id: number) => false as boolean,
        takeFilteredTrackSkip: () => {},
        getBulkSkippedData: () => new Map(),
        getStagedData: () => new Map(),
      }
    }
    const resetStagedFields = () => {
      setStates(v => ({...v, staged: { ids: [] }}))
      stagedDataRef.current.clear()
      bulkSkippedDataRef.current.clear()
    }

    const addFieldToEditor = (x:FieldDto) => {
      if (!groupedFields.unchartedIds.has(x.fieldId!) 
      || stagedDataRef.current.get(x.fieldId!)) { return; }
  
      setStates(v => {
        stagedDataRef.current.set(x.fieldId, x)
        bulkSkippedDataRef.current.delete(x.fieldId)
        return ({ ...v,
          staged: {
            ids: [...stagedDataRef.current.values()].sort(fieldsSorter).map(s => s.fieldId!),
          }
        })
      })
    }

    const removeFieldFromEditor = (id:number) => {
      if (!stagedDataRef.current.has(id)) { return }
      setStates(v => {
        stagedDataRef.current.delete(id)
        return { ...v,
          staged: { 
            ids: [...stagedDataRef.current.values()].sort(fieldsSorter).map(s => s.fieldId!),
          }
        }
      })
      propsRef.current.onRemoveFieldFromEditor?.(id)
    }

    const isStagedField = (id:number) => stagedDataRef.current.has(id)

    const takeFilteredTrackSkip = (trackSkip: (ls: FieldDto[])=>FieldDto[]) => {
      const toSkip = trackSkip(groupedOptionsRef.current)
      for (const dto of toSkip) {
        bulkSkippedDataRef.current.set(dto.fieldId, dto)
      }
      setStates(v => ({...v}))
    }

    const getBulkSkippedData = () => bulkSkippedDataRef.current as ReadonlyMap<number, FieldDto>
    const getStagedData = () => stagedDataRef.current as ReadonlyMap<number, FieldDto>

    return {
      resetStagedFields, addFieldToEditor, removeFieldFromEditor,
      isStagedField, takeFilteredTrackSkip, getBulkSkippedData, getStagedData,
    }
  }, [bigTableModeOn, groupedFields.unchartedIds])

  useEffect(() => {
    modifiers.resetStagedFields()
  }, [ids, modifiers])

  const {
    slots, groupedOptions,
  } = useBigTableSupportUISlots({ bigTableModeOn, states, setStates, groupedFields, addFieldToEditor: modifiers.addFieldToEditor })
  groupedOptionsRef.current = groupedOptions as any // single select

  const editorOnlyListCtx: ListControllerProps<FieldDto & RecPatch> = useMemo(() => {
    const newIds = [
      ...states.staged.ids, 
      ...(groupedFields.charted.map(x => x.fieldId!))
    ]
    const newData = {
      ...Object.fromEntries(stagedDataRef.current.entries()), 
      ...(groupedFields.charted.reduce((acc, x) => { 
        acc[x.fieldId!] = x; 
        return acc
      }, {} as any))
    }
    return { ...listCtx, ids: newIds, data: newData, } as any
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedFields.charted, states.staged])

  return {
    bigTableModeOn,
    slots,
    editorOnlyListCtx: bigTableModeOn ? editorOnlyListCtx : {} as any,
    modifiers,
  }
}

interface BigTableUIStates {
    popoutOpen: boolean;
    staged: { ids: number[]; };
}

const useBigTableSupportUISlots = ({
  bigTableModeOn, states, setStates, groupedFields, addFieldToEditor,
}: {
  bigTableModeOn: boolean,
  states: BigTableUIStates, 
  setStates: Dispatch<SetStateAction<BigTableUIStates>>,
  groupedFields: {
    charted: FieldDto[];
    uncharted: FieldDto[];
    unchartedIds: Set<number>;
  },
  addFieldToEditor: (x: FieldDto) => void,
}) => {
  // when not in bigTableMode, useAutocomplete hook still expects an input element as ref,
  // we give it an input element not being used so the hook won't fail
  const nonActionInputRef = useRef({ 
    input: document.createElement('input'),
    getInputProps: () => ({ ref:{current:null}}) as any,
  })
  useEffect(() => {
    if (!bigTableModeOn) {
      const {input, getInputProps} = nonActionInputRef.current
      const inputRef : MutableRefObject<any> = (getInputProps() as any).ref
      inputRef.current = input
    }
  }, [bigTableModeOn])
  useEffect(() => {
    return () => {
      nonActionInputRef.current.input = null as any
      nonActionInputRef.current.getInputProps = null as any
      nonActionInputRef.current = null as any
    }
  }, [])

  const clearRef = useRef((x:any)=>{})

  const {
    getRootProps,
    getInputLabelProps,
    setAnchorEl,
    getClearProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    id,
    groupedOptions,
    inputValue,
  } = useAutocomplete({ 
    open: states.popoutOpen,
    clearOnBlur: false,
    openOnFocus: true,
    options: groupedFields.uncharted,
    getOptionLabel,
    onChange: (event, newValue, reason, details) => {
      if (reason === 'selectOption' && newValue) {
        addFieldToEditor(newValue)
        setTimeout(() => clearRef.current({}), 5)
      }
    },
    onOpen: () => setStates(v => ({...v, popoutOpen: true })),
    onClose: (event, reason) => {
      if (reason !== 'blur' && reason !== 'toggleInput') { 
        setStates(v => ({...v, popoutOpen: false })) 
      }
    }
  })
  
  clearRef.current = getClearProps().onClick!
  nonActionInputRef.current.getInputProps = getInputProps
  // check again just in case
  useEffect(() => {
    if (!bigTableModeOn) {
      const {input, getInputProps} = nonActionInputRef.current
      const inputRef : MutableRefObject<any> = (getInputProps() as any).ref
      inputRef.current = input
    }
  }, [bigTableModeOn])

  const overwriteRootProp = (props: any) => {
    const { onKeyDown: originKD, ...rest } = props
    const onKeyDown = (event: KeyboardEvent) => {
      const o = { key: '', preventDefault: () => {}}
      switch (event.key) {
        case 'PageUp': { 
          o.key = 'PageUp'  
          for(let i = 0; i < 2; i++) { originKD(o) }
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'PageDown': { 
          o.key = 'PageDown'  
          for(let i = 0; i < 2; i++) { originKD(o) }
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'ArrowUp': { 
          o.key = 'ArrowUp'  
          originKD(o); originKD(o);
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'ArrowDown': { 
          o.key = 'ArrowDown'  
          originKD(o); originKD(o);
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'ArrowLeft': { 
          o.key = 'ArrowUp'
          event.preventDefault()
          originKD(o)
          break; 
        }
        case 'ArrowRight': { 
          o.key = 'ArrowDown'
          event.preventDefault()
          originKD(o)
          break; 
        }
        default: {
          originKD(event)
          break;
        }
      }
    }
    return {...rest, onKeyDown}
  }

  const SearchInput = !bigTableModeOn ? null :
    <Box {...overwriteRootProp(getRootProps())}>
      <MuiTextField 
        {...({
          id,
          placeholder: 'Search & add mapping...',
          size: 'small',
          InputLabelProps: getInputLabelProps(),
          InputProps: {
            ref: setAnchorEl,
            endAdornment: (
              <Box>
                <IconButton tabIndex={-1} sx={{ visibility: states.popoutOpen ? 'visible' : 'hidden' }}
                  onClick={(event:any) => {  
                    setStates(v => ({...v, popoutOpen:false}))
                    const clear = getClearProps().onClick!
                    clear(event)
                  }}
                  aria-label="Clear"
                  title="Clear"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            ),
          },
          inputProps: {
            ...getInputProps(),
          },
        })}
      />
    </Box>
  const ListBox = !bigTableModeOn ? null :
    <Box sx={{ position: 'relative' }}>
      <Box component="ul" {...getListboxProps()}
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          maxHeight: 440, overflowY: 'auto',
          mt: 0,
          bgcolor: theme => alpha(theme.palette.success.light, 0.05),
          '& > li': {
            listStyle: 'none',
            mr: 2,
            p: 0.25,
            '&.Mui-focused': { bgcolor: 'action.hover' },
            '&[aria-disabled="true"]': {opacity: 'action.disabledOpacity', pointerEvents: 'none'},
            '&.Mui-focusVisible': { bgcolor: 'action.focus' },
            '&[aria-selected="true"]': {
              bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
              '&.Mui-focused': { bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity) },
              '&.Mui-focusVisible': { bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.focusOpacity) },
            },
          }
        }}
      >
        {groupedOptions.map((option:any, index:number) => {
          const fieldname = getOptionLabel(option as any)
          const matches = match(fieldname, inputValue, {insideWords:true});
          const parts = parse(fieldname, matches);
          return (
            // @ts-expect-error hhh
            <li key={(option as any).fieldId} {...getOptionProps({option, index} as any)}>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400}}>
                  {part.text}
                </span>
              ))}
            </li>)
        })}
      </Box>
    </Box>
  return {
    groupedOptions,
    slots: { 
      SearchInput, ListBox,
      popoutOpen: states.popoutOpen,
      filteredItemCount: groupedOptions.length,
      closePopout: () => setStates(v => ({...v, popoutOpen: false})),
    },
  }
}

const getOptionLabel = (x:FieldDto) => x.fieldName!
